import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import type { BillingPlanTier } from '@core/billing-types';

import { FeatureGuardContentComponent } from '../feature-guard-content/feature-guard-content.component';
import { FeatureGuardFacade } from '../feature-guard.facade';
import type { FeatureData } from '../feature-guard.model';
import { FeaturesGuardModel } from '../feature-guard.model';
import type { GuardedFeature } from '../feature-guard.types';

@Component({
  imports: [FeatureGuardContentComponent],
  selector: 'rt-feature-guard-empty',
  templateUrl: './feature-guard-empty.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./feature-guard-empty.component.less'],
})
export class FeatureGuardEmptyComponent {
  private readonly store = inject(Store);
  private readonly model = inject(FeaturesGuardModel);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly guardFacade = inject(FeatureGuardFacade);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly customUpgradeButton = input<TemplateRef<any>>();

  public featureData: FeatureData;
  public canUpgrade: boolean = false;
  public paddingTopLarge: boolean = false;
  public availableFromPlan: BillingPlanTier | null;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    const feature: GuardedFeature = this.route.snapshot.data.feature;
    const paddingTopLarge: boolean = this.route.snapshot.data.paddingTopLarge;

    if (feature) {
      this.featureData = this.model.data[feature];
      this.store
        .select(AbilitiesSelectors.hasOne(Ability.ManageSubscription))
        .pipe(take(1))
        .subscribe(canManageSubscription => {
          this.canUpgrade = canManageSubscription;
        });

      this.guardFacade
        .getPlanWhereFeatureIsAvailable(feature)
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(availableFromPlan => {
          this.availableFromPlan = availableFromPlan;
          this.cdRef.detectChanges();
        });

      if (this.featureData.analytics) {
        this.featureData.analytics.discrimination();
      }
    }

    this.paddingTopLarge = paddingTopLarge || false;
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public openPlanPicker() {
    this.router.navigate([{ outlets: { dialog: ['order-2024', 'select-product'] } }]);

    if (this.featureData.analytics) {
      this.featureData.analytics.cta();
    }
  }
}
